/**
 * The `Floe()` config boundary: public {@link FloeConfig} -> internal SDKConfig.
 *
 * Split out of index.ts so it can be unit tested (see tests/index-config.test.ts).
 * It deliberately imports NOTHING heavy — `resolveActivation` and two types —
 * because the whole point is to be loadable without the React UI tree.
 */

import { resolveActivation } from './agent-mode';
import { resolveExitIntentConfig } from './exit-intent';
import type { SDKConfig } from './types';
// Type-only: erased at build time, so this is not a runtime import cycle.
import type { FloeConfig } from './index';

/**
 * Clone the optional MCP boundary without trusting TypeScript at runtime.
 * Customer snippets are plain JavaScript, so a missing/non-array allowlist
 * must reach the fail-open session validator rather than throwing during
 * Floe() initialization.
 */
function cloneMcpConfig(config: unknown): SDKConfig['mcp'] {
  if (typeof config !== 'object' || config === null || Array.isArray(config)) {
    return undefined;
  }

  const raw = config as Record<string, unknown>;
  return {
    serverUrl: raw.serverUrl as string,
    getBearerToken: raw.getBearerToken as NonNullable<SDKConfig['mcp']>['getBearerToken'],
    allowedTools: (
      Array.isArray(raw.allowedTools) ? [...raw.allowedTools] : raw.allowedTools
    ) as NonNullable<SDKConfig['mcp']>['allowedTools'],
  };
}

/**
 * Convert FloeConfig to internal SDKConfig with proper defaults
 * Note: apiUrl is only included if explicitly provided, allowing the SDK
 * constructor to use its build-time default (__FLOE_API_URL__)
 *
 * ⚠️ THIS MAP IS EXPLICIT AND HAS NO SPREAD. A key that exists on SDKConfig, is
 * read by OnboardingSDK, and is passed by an embed still reaches NOTHING unless
 * it is listed both on {@link FloeConfig} above and in the object below. Nothing
 * type-checks that: the `/d/[token]` shell builds its options as
 * `Record<string, unknown>` and calls a `Floe: (opts: unknown)` cast, so an
 * unmapped key is dropped in silence.
 *
 * That is not hypothetical — `demoLinkId`, `demoCalendarLink` and
 * `demoIdentityCapture` were all missing here, which killed every per-link
 * override in production. Exported solely so index-config.test.ts can pin the
 * passthrough; nothing outside the tests should call it.
 */
export function convertFloeToSDKConfig(config: FloeConfig): SDKConfig {
  const sdkConfig: SDKConfig = {
    clientKey: config.clientKey,
    demoMode: config.demoMode ?? false,
    // FLO-320: website-agent mode is opt-in and OFF by default.
    // demoMode takes precedence — a customer who sets both gets the demo agent,
    // not the website-agent (matches the FloeConfig.websiteAgent contract, and
    // keeps every downstream websiteAgent check — init branch + AgentUI — off).
    //
    // FLO-451: these two flags are the ONLY input to `drivesHostPage()`
    // (agent-mode.ts) — the positive capability that gates all host-page
    // telemetry. OnboardingSDK derives it once in its constructor rather than
    // reading a stamped config field, so an SDK constructed outside this
    // boundary can't end up with it `undefined` (which would silently mute
    // the onboarding agent). Add a mode here → teach that predicate about it.
    websiteAgent: config.demoMode ? false : (config.websiteAgent ?? false),
    // FLO-514/519: sidebar display mode. Deliberately NOT defaulted here —
    // `undefined` means "no init-time override", which is what lets the per-site
    // value from validate-client-key apply. The floating/400 defaults are
    // resolved once, at the render layer (websiteAgentLayout), so a junk server
    // value and a missing one land on the same safe answer.
    websiteAgentDisplayMode: config.websiteAgentDisplayMode,
    websiteAgentSidebarWidth: config.websiteAgentSidebarWidth,
    // Undefined means "use the default"; resolveAvatarIdleAfterMs owns the
    // clamp so a junk value and a missing one land on the same answer.
    avatarIdleAfterMs: config.avatarIdleAfterMs,
    demoSiteId: config.demoSiteId,
    enableVideo: false, // Video not used in onboarding; demo mode receives video via trackStarted
    enableAudio: config.enableAudio ?? true,
    enableScreenCapture: config.demoMode ? false : (config.enableScreenCapture ?? true),
    debug: config.debug ?? false,
    industry: config.industry,
    useCase: config.useCase,
    companyName: config.companyName,
    companySize: config.companySize,
    role: config.role,
    userInfo: config.userInfo,
    skipOnboardingModal: config.skipOnboardingModal,
    // FLO-534: deliberately a literal, NOT a passthrough of an `enableDiscoveryPopup`
    // input. This flag only chooses which *proactive* opening to use; turning it
    // off does not make the agent dormant — `init()` would connect anyway, just
    // without the popup, which is strictly worse than today. `activation` below
    // is the customer-facing switch for "should the agent ever speak first?".
    enableDiscoveryPopup: true,
    activation: resolveActivation(config.activation),
    // FLO-661: preserve the token PROVIDER, not a token snapshot. Clone the
    // allowlist so a host mutating its init object later cannot silently widen
    // the tools exposed by an already-created widget.
    mcp: cloneMcpConfig(config.mcp),
    // Apply nudge defaults at SDK boundary to enforce documented contract.
    //
    // FLO-534: `autoShow` is the ONE exception and is deliberately left
    // un-defaulted. Its default depends on `activation` — support mode flips it
    // off — so it is resolved downstream by resolveNudgeAutoShow(), for which
    // `undefined` means "the customer didn't say". Materialising `true` here
    // made that resolver unreachable for any embed that passed a nudge object
    // at all: `{ activation: 'on_demand', nudge: { text: 'Need a hand?' } }`
    // — customising only the copy — still slid the bubble out 5s after load.
    nudge: config.nudge ? {
      text: config.nudge.text ?? 'Hi 👋 How can I help you today?',
      autoShow: config.nudge.autoShow,
      autoHideDelay: config.nudge.autoHideDelay ?? 0,
    } : undefined,
    // Exit intent is opt-in. Clone + normalize the plain-JS boundary so a host
    // cannot mutate a live SDK's dwell/copy after initialization, and malformed
    // values fail closed instead of breaking Floe().
    exitIntent: resolveExitIntentConfig(config.exitIntent) ?? false,
    // FLO-585: launcher corner/inset. Deliberately NOT defaulted here — the
    // render layer owns the 'bottom-right' / 24px defaults so a
    // junk value and a missing one land on the same safe answer.
    launcher: config.launcher,
    prospectEmail: config.prospectEmail,
    // FLO-285: marketing embeds default to the bottom-docked pill. Dedicated
    // demo pages opt into 'fullscreen' explicitly.
    embedMode: config.embedMode ?? 'docked',
    // Consent mode. Deliberately NOT defaulted here — `undefined` means "no
    // init-time choice", which is what lets the per-site
    // `websiteAgent.consentModeDefault` from validate-client-key apply
    // (resolveConsentMode folds junk/absent to 'not-required' downstream).
    consentMode: config.consentMode,
    // Per-link demo options. Straight passthrough — `undefined` has to survive
    // as `undefined` for all three: it is what lets the SITE-level value (from
    // validate-client-key) apply, exactly like websiteAgentDisplayMode above.
    // Defaulting any of them here would make that fallback unreachable.
    demoLinkId: config.demoLinkId,
    demoCalendarLink: config.demoCalendarLink,
    demoIdentityCapture: config.demoIdentityCapture,
  };

  // An own `apiUrl: undefined` would suppress the SDK constructor's build-time
  // default, so add the key only for an actual caller-provided override.
  if (config.apiUrl !== undefined) {
    sdkConfig.apiUrl = config.apiUrl;
  }

  return sdkConfig;
}
